// WhitewashReportModal — PII Audit Report via the agent pipeline.
//
// Flow:
//   - On open: GET /tools/whitewash/report/{document_id}.
//     * If a cached report_markdown exists -> show it (markdown-rendered) +
//       "Download .docx" + "Regenerate" buttons.
//     * If no cached report (404) -> show a "Generate PII report" button
//       that POSTs .../generate (30-90s; loading spinner).
//   - Always show the itemized items_stripped summary + zero-PII
//     confirmation + sanitized hash (the audit metadata).
//   - Errors surface the backend message (e.g. "No PII-stripped text..."
//     / "Agent pipeline unavailable...").

import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  X,
  Loader2,
  AlertTriangle,
  FileDown,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import {
  getWhitewashReport,
  generatePiiReport,
  downloadPiiReportDocx,
  type WhitewashReportItem,
} from '../api/datawash';
import MarkdownRenderer from './MarkdownRenderer';

interface WhitewashReportModalProps {
  documentId: string;
  documentName?: string;
  onClose: () => void;
}

type ItemsShape = WhitewashReportItem[] | Record<string, number> | null;

function itemsToRows(items: ItemsShape): { category: string; count: number }[] {
  if (!items) return [];
  if (Array.isArray(items)) {
    return items.map((it) => ({ category: it.category, count: it.count }));
  }
  // dict {category: count}
  return Object.entries(items).map(([category, count]) => ({
    category,
    count: Number(count) || 0,
  }));
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default function WhitewashReportModal({
  documentId,
  documentName,
  onClose,
}: WhitewashReportModalProps) {
  const [items, setItems] = useState<ItemsShape>(null);
  const [zeroPii, setZeroPii] = useState<boolean | null>(null);
  const [hash, setHash] = useState<string>('');
  const [reportMarkdown, setReportMarkdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load cached report metadata (instant re-open when already generated).
  const loadReport = () => {
    setLoading(true);
    setError(null);
    getWhitewashReport(documentId)
      .then((report) => {
        setItems((report.items_stripped as ItemsShape) || null);
        setZeroPii(report.zero_pii_confirmed ?? null);
        setHash(report.sanitized_hash || '');
        setReportMarkdown(report.report_markdown || null);
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          // No cached report yet -- show the generate button.
          setReportMarkdown(null);
          setItems(null);
          setZeroPii(null);
          setHash('');
        } else {
          setError(e?.response?.data?.detail || 'Failed to load PII report');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const handleGenerate = () => {
    setGenerating(true);
    setError(null);
    generatePiiReport(documentId)
      .then((res) => {
        setItems((res.items_stripped as ItemsShape) || null);
        setZeroPii(res.zero_pii_confirmed ?? null);
        setHash(res.sanitized_hash || '');
        setReportMarkdown(res.report_markdown || null);
      })
      .catch((e) => {
        setError(
          e?.response?.data?.detail ||
            'Agent pipeline call failed. The bridge may be unavailable.',
        );
      })
      .finally(() => setGenerating(false));
  };

  const handleDownloadDocx = () => {
    setDownloading(true);
    setError(null);
    downloadPiiReportDocx(documentId)
      .then((blob) => {
        const stem = (documentName || 'pii-report').replace(/\.[^.]+$/, '');
        triggerBlobDownload(blob, `${stem}-pii-report.docx`);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Failed to download .docx');
      })
      .finally(() => setDownloading(false));
  };

  const rows = itemsToRows(items);
  const hasReport = !!reportMarkdown && reportMarkdown.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="whitewash-report-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-primary-600" size={20} />
            <h2 className="text-lg font-semibold text-slate-900">PII Audit Report</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {documentName && (
            <div className="text-sm text-slate-500 mb-3">
              Document:{' '}
              <span className="text-slate-800 font-medium">{documentName}</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
              {error}
            </div>
          )}

          {!loading && !hasReport && !generating && !error && (
            <div className="py-6 text-center">
              <p className="text-sm text-slate-600 mb-4">
                No PII audit report yet. Generate one via the agent pipeline
                (stripped data → Claude → reconstructed report). The call can
                take 30–90 seconds.
              </p>
              <button
                onClick={handleGenerate}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="pii-report-generate"
              >
                <Sparkles size={16} /> Generate PII report
              </button>
            </div>
          )}

          {generating && (
            <div className="py-8 flex flex-col items-center text-slate-500">
              <Loader2 size={20} className="animate-spin mb-2" />
              <p className="text-sm">Generating via agent pipeline…</p>
            </div>
          )}

          {hasReport && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={handleDownloadDocx}
                  disabled={downloading}
                  className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pii-report-download-docx"
                >
                  {downloading ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <FileDown size={15} />
                  )}
                  Download .docx
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pii-report-regenerate"
                  title="Re-run the agent pipeline (re-calls Claude)"
                >
                  {generating ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <RefreshCw size={15} />
                  )}
                  Regenerate
                </button>
              </div>
              <div
                className="prose prose-sm max-w-none border border-slate-200 rounded-lg p-4 bg-slate-50"
                data-testid="pii-report-markdown"
              >
                <MarkdownRenderer content={reportMarkdown || ''} />
              </div>
            </>
          )}

          {!loading && (rows.length > 0 || zeroPii !== null) && (
            <div className="mt-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">
                Items stripped
              </h3>
              <table className="w-full text-sm" data-testid="whitewash-items">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2">Category</th>
                    <th className="py-2 text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-2 capitalize text-slate-700">
                          {r.category}
                        </td>
                        <td className="py-2 text-right text-slate-700">
                          {r.count}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={2}
                        className="py-4 text-center text-slate-400"
                      >
                        No PII items recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div
                className="mt-4 flex items-center gap-2 text-sm"
                data-testid="report-zero-pii"
              >
                {zeroPii ? (
                  <ShieldCheck size={16} className="text-green-600" />
                ) : (
                  <AlertTriangle size={16} className="text-amber-600" />
                )}
                <span className="text-slate-600">Zero PII confirmed:</span>
                <span
                  className={
                    zeroPii
                      ? 'text-green-700 font-medium'
                      : 'text-amber-700 font-medium'
                  }
                >
                  {zeroPii ? 'YES' : 'NO'}
                </span>
              </div>
              <div
                className="mt-2 text-sm text-slate-600"
                data-testid="report-hash"
              >
                Sanitized hash:{' '}
                <span className="font-mono text-xs text-slate-800">
                  {hash || '—'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}